import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LabelsService from "../../../services/LabelsService";
import { BRAND_COLOR, BRAND_COLOR_DARK, getErrorMessage, getUsageTotal, normalizeText } from "./labelUtils";

const LabelFormDialog = ({ open, label, existingLabels = [], onClose, onSaved }) => {
  const isEditing = Boolean(label);
  const [name, setName] = useState("");
  const [isProfile, setIsProfile] = useState(false);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(label?.name || "");
      setIsProfile(Boolean(label?.isProfile));
      setError("");
    }
  }, [open, label]);

  const trimmedName = name.trim();
  const usageTotal = getUsageTotal(label?.usage);
  const nameChanged = isEditing && trimmedName !== label.name;
  const isDuplicate = existingLabels.some(
    (item) => item.id !== label?.id && normalizeText(item.name) === normalizeText(trimmedName)
  );

  const validationError = !trimmedName
    ? "El nombre es obligatorio."
    : trimmedName.includes(",")
    ? "El nombre no puede contener comas."
    : isDuplicate
    ? "Ya existe una etiqueta con ese nombre."
    : "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSaving(true);
      const payload = { name: trimmedName, isProfile };
      if (isEditing) {
        await LabelsService.updateLabel(label.id, payload);
        onSaved?.(`Etiqueta "${trimmedName}" actualizada correctamente.`);
      } else {
        await LabelsService.createLabel(payload);
        onSaved?.(`Etiqueta "${trimmedName}" creada correctamente.`);
      }
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo guardar la etiqueta."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEditing ? "Editar etiqueta" : "Nueva etiqueta"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Nombre de la etiqueta"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setError("");
              }}
              autoFocus
              fullWidth
              required
              error={Boolean(error) || (Boolean(trimmedName) && Boolean(validationError))}
              helperText={error || (trimmedName ? validationError : "")}
            />

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isProfile}
                    onChange={(event) => setIsProfile(event.target.checked)}
                    color="secondary"
                  />
                }
                label={<Typography fontWeight={600}>Etiqueta de perfil</Typography>}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                Las etiquetas de perfil describen el rol o especialidad principal del usuario. Las demás
                son etiquetas de interés y se pueden agrupar.
              </Typography>
            </Box>

            {nameChanged && usageTotal > 0 && (
              <Alert severity="info">
                El nuevo nombre se reflejará automáticamente en los <strong>{usageTotal}</strong>{" "}
                registros donde se usa esta etiqueta (cursos, publicaciones, videos, documentos, usuarios y
                grupos).
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={isSaving} color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving || Boolean(validationError)}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: BRAND_COLOR_DARK } }}
          >
            {isEditing ? "Guardar cambios" : "Crear etiqueta"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default LabelFormDialog;
